import {
    Box,
    Button,
    Grid,
    Card,
    CardContent,
    Typography,
    IconButton,
    Chip,
    Stack,
    CircularProgress,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { DELETE_PRODUCT } from 'src/graphql/mutations/products';
import { useSnackbar } from 'notistack';
import ProductDialog from './ProductDialog';

export default function ProductManagement({ products, loading, refetch }) {
    const [openDialog, setOpenDialog] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    
    const [deleteProduct, { loading: deleteLoading }] = useMutation(DELETE_PRODUCT, {
        onCompleted: () => {
            enqueueSnackbar('Product deleted successfully!', { variant: 'success' });
            refetch();
        },
        onError: (error) => {
            enqueueSnackbar(error.message || 'Failed to delete product', { variant: 'error' });
        },
    });

    const handleOpenDialog = (product = null) => {
        setEditingProduct(product);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setEditingProduct(null);
        setOpenDialog(false);
    };

    const handleDelete = (productId) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            deleteProduct({ variables: { productId } });
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="flex-end" mb={3}>
                <Button
                    variant="contained"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    onClick={() => handleOpenDialog()}
                >
                    Create New Product
                </Button>
            </Stack>

            <Grid container spacing={3}>
                {products?.map((product) => (
                    <Grid item xs={12} md={6} lg={4} key={product._id}>
                        <Card
                            sx={{
                                position: 'relative',
                                height: '100%',
                                '&:hover': {
                                    boxShadow: 6,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    display: 'flex',
                                    gap: 1,
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog(product)}
                                    sx={{ bgcolor: 'background.paper' }}
                                >
                                    <Iconify icon="eva:edit-2-fill" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => handleDelete(product._id)}
                                    disabled={deleteLoading}
                                    sx={{ bgcolor: 'background.paper' }}
                                >
                                    <Iconify icon="eva:trash-2-fill" />
                                </IconButton>
                            </Box>

                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 1,
                                            bgcolor: product.isActive ? 'success.main' : 'error.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 2,
                                        }}
                                    >
                                        <Iconify
                                            icon="eva:shopping-bag-outline"
                                            sx={{ color: 'white', width: 28, height: 28 }}
                                        />
                                    </Box>
                                    <Box flex={1}>
                                        <Typography variant="h6" noWrap>
                                            {product.productName}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Stack direction="row" spacing={1} mb={2}>
                                    <Chip
                                        label={product.isActive ? 'Active' : 'Inactive'}
                                        size="small"
                                        color={product.isActive ? 'success' : 'error'}
                                    />
                                    <Chip
                                        label={product.isAddOn ? 'Add-on' : 'Base Product'}
                                        size="small"
                                        color={product.isAddOn ? 'secondary' : 'primary'}
                                    />
                                </Stack>

                                <Typography variant="h5" color="primary" mb={2}>
                                    ₦{product.amount?.toLocaleString()}
                                </Typography>

                                {product.description && (
                                    <Typography variant="body2" color="text.secondary" mb={2} sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                    }}>
                                        {product.description}
                                    </Typography>
                                )}

                                {product.questions?.length > 0 && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Questions: {product.questions.length}
                                        </Typography>
                                    </Box>
                                )}

                                {product.scope?.length > 0 && (
                                    <Box mt={1}>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                            Scope:
                                        </Typography>
                                        {product.scope.map((item, index) => (
                                            <Chip
                                                key={index}
                                                label={item}
                                                size="small"
                                                variant="outlined"
                                                sx={{ m: 0.25 }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}

                {products?.length === 0 && (
                    <Grid item xs={12}>
                        <Box textAlign="center" py={5}>
                            <Iconify icon="eva:inbox-outline" sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No products found. Create your first product!
                            </Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>

            <ProductDialog
                open={openDialog}
                onClose={handleCloseDialog}
                product={editingProduct}
                refetch={refetch}
            />
        </Box>
    );
}
